import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Avatar,
  Stack,
  MenuItem,
  Menu,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  styled,
  Tooltip,
  Fade,
} from "@mui/material";

// Icons
import NotificationsIcon from "@mui/icons-material/Notifications";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import ArticleIcon from "@mui/icons-material/Article";

import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import useUser from "context/user/useUser";
import { TimeChangeModal } from "./TimeChangeModal";
import { PrescriptionModal } from "./PrescriptionModal";
import { api } from "services/ApiService";
import {
  getProfilePic,
  getUserEmail,
  getUserName,
  logout,
} from "auth/authenticate";
import { onMessageListener } from "../../../firebase";
import { Store } from "react-notifications-component";
import avatarImg from "./images/avatar.png";
import deemedLogo from "./images/dark-logo.png";
// import deemedLogoM from "./images/logom.png";
import brandLogo from "./images/brand.png";
import {
  AddToQueue,
  CreditCard,
  Dashboard,
  Favorite,
  NewspaperRounded,
  Payments,
  PermIdentity,
  Phone,
  Search,
} from "@mui/icons-material";
import { IS_CHRISTMAS } from "config/eventsUpdate/seasonal";

// Styled components for enhanced design
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: "blur(10px)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  color: theme.palette.text.primary,
  transition: "all 0.3s ease",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginRight: theme.spacing(8),
  "& img": {
    height: "auto",
    transition: "all 0.3s ease",
  },
  "& .desktop-logo": {
    maxHeight: 36,
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  "& .mobile-tablet-logo": {
    maxHeight: 32,
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "block",
    },
  },
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.95rem",
  padding: theme.spacing(0.5, 1.5),
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  marginRight: theme.spacing(1),
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  "&:after": active
    ? {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "60%",
        height: 3,
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px 3px 0 0",
      }
    : {},
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const ProfileContainer = styled(Stack)(({ theme }) => ({
  background: `linear-gradient(90deg, ${alpha(
    theme.palette.primary.light,
    0.1
  )}, ${alpha(theme.palette.background.paper, 0.1)})`,
  borderRadius: 24,
  padding: theme.spacing(0.5, 1),
  marginLeft: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    background: `linear-gradient(90deg, ${alpha(
      theme.palette.primary.light,
      0.15
    )}, ${alpha(theme.palette.background.paper, 0.15)})`,
    transform: "translateY(-2px)",
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: `0 0 0 2px ${alpha(theme.palette.background.paper, 0.8)}`,
  width: 40,
  height: 40,
  transition: "all 0.2s ease",
}));

const NotificationBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.error.main,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    padding: "0 6px",
    fontSize: "0.7rem",
    fontWeight: "bold",
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
}));

// remove read option
// const NotificationItem = styled(MenuItem)(({ theme, read }) => ({
//   padding: theme.spacing(1.5, 2),
//   borderLeft: read ? "none" : `4px solid ${theme.palette.primary.main}`,
//   backgroundColor: read
//     ? "transparent"
//     : alpha(theme.palette.primary.light, 0.05),
//   "&:hover": {
//     backgroundColor: alpha(theme.palette.primary.light, 0.1),
//   },
// }));

const NotificationItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.1),
  },
}));

export default function ModernMenuBar({ appNotifications }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const username = getUserName();
  const useremail = getUserEmail();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const { settings, userRole, setUserRole } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showTimeChangeModal, setShowTimeChangeModal] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);

  useEffect(() => {
    if (userRole === "PATIENT") getNotifications();
  }, [userRole]);

  useEffect(() => {
    onMessageListener()
      .then((payload) => {
        Store.addNotification({
          title: payload.notification.title,
          message: payload.notification.body,
          type: "default",
          insert: "top",
          container: "top-right",
          dismiss: {
            duration: 8000,
            onScreen: true,
          },
        });
        // Refresh notifications
        getNotifications();
      })
      .catch((err) => console.log("Notification listener error:", err));
  }, []);

  const getNotifications = async () => {
    try {
      const response = await api(
        `${process.env.REACT_APP_BACKEND_URL}notification`
      );
      const data = response?.data?.data;
      // alert("This is notifications. -> " + JSON.stringify(data));

      if (data) {
        setNotifications(data);
        // const unreadCount = data.filter((item) => item.status === 1).length;
        // setNotificationCount(unreadCount);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleNotificationClick = (modalType, data, id) => {
    setNotificationAnchorEl(null);

    // Mark notification as read
    if (id) {
      api(`${process.env.REACT_APP_BACKEND_URL}notification/${id}`, "PUT", {
        status: 0,
      }).then(() => {
        getNotifications();
      });
    }

    // Handle specific modal actions
    if (modalType === "TIME_CHANGE") {
      setShowTimeChangeModal(true);
    } else if (modalType === "PRESCRIPTION") {
      setShowPrescriptionModal(true);
    }
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const navItems = [
    { label: "Home", path: "/", icon: <DashboardIcon /> },
    // { label: "Find a Doctor", path: "/", icon: <Search /> },
    { label: "Doctors", path: "/doctors", icon: <MedicalServicesIcon /> },
    { label: "News", path: "/news", icon: <NewspaperRounded /> },
    { label: "Join", path: "/join", icon: <HealthAndSafetyIcon /> },
    { label: "About", path: "/about", icon: <PersonIcon /> },
    { label: "Contact Us", path: "/contact", icon: <Phone /> },

    // {
    //   label: "Appointments",
    //   path: "/appointments",
    //   icon: <CalendarMonthIcon />,
    // },
    // { label: "Health Records", path: "/records", icon: <ArticleIcon /> },
    // { label: "Services", path: "/services", icon: <HealthAndSafetyIcon /> },
  ];

  const profileMenuItems = [
    {
      label: "Dashboard",
      icon: <AddToQueue />,
      action: () => navigate("/patient-dashboard"),
    },
    {
      label: "My Appointments",
      icon: <CalendarMonthIcon />,
      action: () => navigate("/appointments"),
    },
    // {
    //   label: "Medical Records",
    //   icon: <ArticleIcon />,
    //   action: () => navigate("/records"),
    // },
    {
      label: "My Profile",
      icon: <PersonIcon />,
      action: () => navigate("/patient-profile"),
    },
    {
      label: "Favourites",
      icon: <Favorite />,
      action: () => navigate("/favourites"),
    },
    {
      label: "Payments",
      icon: <CreditCard />,
      action: () => navigate("/patient-settings/payments"),
    },

    // Conditionally add Log Out item if username exists
    ...(username
      ? [
          {
            label: "Log Out",
            icon: <LogoutIcon />,
            action: () => {
              /* Implement logout logic */
              logout();
              setUserRole("GUEST");
              navigate("/login");
            },
          },
        ]
      : []),
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawerContent = (
    <Box sx={{ width: 280 }} role="presentation">
      <DrawerHeader>
        <LogoContainer>
          <img src={deemedLogo} alt="Deemed Logo" height="25" width="100%" />
          {IS_CHRISTMAS && (
          <img
            src="/seasonal/christmas/santa-hat.png"
            alt="christmas"
            style={{
              position: "absolute",
              top: 1,
              left: 2,
              width: 36,
              transform: "rotate(-18deg)",
              pointerEvents: "none",
            }}
          />
          )}
        </LogoContainer>
        <IconButton onClick={toggleDrawer(false)}>
          <CloseIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />

      {/* User profile in drawer */}
      {userRole === "PATIENT" && settings && (
        <>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <UserAvatar
                alt="user avatar"
                src={getProfilePic() || avatarImg}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {username}
                  {/* {settings.username} */}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {/* {settings.email} */}
                  {useremail}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Divider />
        </>
      )}

      {/* Navigation items */}
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.label}
            component={NavLink}
            to={item.path}
            selected={isActive(item.path)}
            onClick={toggleDrawer(false)}
            sx={{
              margin: 1,
              borderRadius: 2,
              backgroundColor: isActive(item.path)
                ? alpha(theme.palette.primary.main, 0.1)
                : "transparent",
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive(item.path)
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: isActive(item.path) ? 600 : 500,
                color: isActive(item.path)
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
              }}
            />
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Auth buttons */}
      {userRole !== "PATIENT" && (
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<LoginIcon />}
            component={NavLink}
            to="/login"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            }}
          >
            Login / Sign Up
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <TimeChangeModal
        show={showTimeChangeModal}
        handleClose={() => setShowTimeChangeModal(false)}
      />

      <PrescriptionModal
        show={showPrescriptionModal}
        handleClose={() => setShowPrescriptionModal(false)}
      />

      <StyledAppBar position="fixed" elevation={0}>
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleDrawer(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <LogoContainer component={NavLink} to="/">
              <img
                alt="Deemed Logo"
                src={deemedLogo}
                className="desktop-logo"
              />

              <img
                alt="Deemed Logo"
                src={brandLogo}
                className="mobile-tablet-logo"
              />

              {IS_CHRISTMAS && (
                <Box
                  component="img"
                  src="/seasonal/christmas/santa-hat.png"
                  alt=""
                  sx={{
                    position: "absolute",
                    top: { xs: 6, sm:6, md: 1 }, // responsive positioning
                    left: { xs: 36, sm:30, md: 1 },
                    width: { xs: 28, md: 36 },
                    transform: "rotate(-18deg)",
                    pointerEvents: "none",
                  }}
                />
              )}
            </LogoContainer>

            {/* Navigation Links */}
            <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "flex" } }}>
              {navItems.map((item) => (
                <NavButton
                  key={item.label}
                  component={NavLink}
                  to={item.path}
                  active={isActive(item.path) ? 1 : 0}
                  startIcon={!isTablet && item.icon}
                  sx={{
                    display: item.path === "/" && isTablet ? "none" : "flex",
                  }}
                >
                  {item.label}
                </NavButton>
              ))}
            </Box>

            {/* Notifications & User Info */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {userRole === "PATIENT" ? (
                <>
                  {/* Notifications */}
                  <Tooltip title="Notifications">
                    <IconButton
                      onClick={handleNotificationMenuOpen}
                      size="medium"
                      color="primary"
                      sx={{
                        backgroundColor:
                          notificationCount > 0
                            ? alpha(theme.palette.primary.main, 0.1)
                            : "transparent",
                        transition: "all 0.2s ease",
                        mr: { xs: 1, md: 2 },
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.15
                          ),
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <NotificationBadge
                        badgeContent={notificationCount}
                        color="error"
                      >
                        <NotificationsIcon />
                      </NotificationBadge>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={notificationAnchorEl}
                    open={Boolean(notificationAnchorEl)}
                    onClose={handleNotificationMenuClose}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        width: 320,
                        maxHeight: 400,
                        overflow: "auto",
                        borderRadius: 2,
                        mt: 1.5,
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.1
                        )}`,
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    TransitionComponent={Fade}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: `1px solid ${alpha(
                          theme.palette.divider,
                          0.1
                        )}`,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600}>
                        Notifications
                      </Typography>
                      {/* {notificationCount > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          You have {notificationCount} unread notification
                          {notificationCount !== 1 ? "s" : ""}
                        </Typography>
                      )} */}
                    </Box>

                    {notifications.length > 0 ? (
                      notifications.map((notification, index) => (
                        <NotificationItem
                          key={notification.id || index}
                          onClick={() =>
                            handleNotificationClick(
                              notification.type,
                              notification.data,
                              notification.id
                            )
                          }
                          // read={notification.status === 0}
                        >
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              sx={{ color: theme.palette.grey[500] }}
                            >
                              {/* {notification.params?.title || "Notification"} */}
                              {notification.params?.details || "Notification"}
                            </Typography>
                            {/* {notification.params?.details && (
                              <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.primary }}
                              >
                                {notification.params.details}
                              </Typography>
                            )} */}
                            {notification.message && (
                              <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                {/* {notification.message.split} */}
                              </Typography>
                            )}
                            {notification.params ? (
                              <Link
                                to={notification.params?.data?.link}
                                style={{
                                  textDecoration: "none",
                                  color: theme.palette.primary.main,
                                  fontWeight: 700,
                                }}
                                onClick={() => setNotificationAnchorEl(null)}
                              >
                                <Typography variant="body1">
                                  {notification.params?.title || "Notification"}
                                </Typography>
                              </Link>
                            ) : (
                              ""
                            )}
                            {notification.createdAt && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {new Date(
                                  notification.createdAt
                                ).toLocaleDateString()}{" "}
                                •{" "}
                                {new Date(
                                  notification.createdAt
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </Typography>
                            )}
                          </Box>
                        </NotificationItem>
                      ))
                    ) : (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          No notifications yet
                        </Typography>
                      </Box>
                    )}

                    {notifications.length > 0 && (
                      <Box
                        sx={{
                          p: 1.5,
                          borderTop: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1
                          )}`,
                          textAlign: "center",
                        }}
                      >
                        {/* <Button
                          size="small"
                          color="primary"
                          onClick={() => {
                            handleNotificationMenuClose();
                            // Mark all as read
                            api(
                              `${process.env.REACT_APP_BACKEND_URL}notification/read-all`,
                              "PUT"
                            ).then(() => {
                              getNotifications();
                            });
                          }}
                        >
                          Mark all as read
                        </Button> */}
                      </Box>
                    )}
                  </Menu>

                  {/* User Profile */}
                  {settings && (
                    <>
                      <ProfileContainer
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        onClick={handleProfileMenuOpen}
                      >
                        <UserAvatar
                          alt={settings.username || "User"}
                          src={getProfilePic() || avatarImg}
                        />
                        <Box sx={{ display: { xs: "none", md: "block" } }}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ fontWeight: 600, maxWidth: 120 }}
                          >
                            {settings.username}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 120 }}
                          >
                            {settings.email}
                          </Typography>
                        </Box>
                      </ProfileContainer>

                      <Menu
                        anchorEl={profileAnchorEl}
                        open={Boolean(profileAnchorEl)}
                        onClose={handleProfileMenuClose}
                        PaperProps={{
                          elevation: 3,
                          sx: {
                            width: 240,
                            borderRadius: 2,
                            mt: 1.5,
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.1
                            )}`,
                          },
                        }}
                        transformOrigin={{
                          horizontal: "right",
                          vertical: "top",
                        }}
                        anchorOrigin={{
                          horizontal: "right",
                          vertical: "bottom",
                        }}
                        TransitionComponent={Fade}
                      >
                        <Box
                          sx={{
                            p: 2,
                            textAlign: "center",
                            borderBottom: `1px solid ${alpha(
                              theme.palette.divider,
                              0.1
                            )}`,
                          }}
                        >
                          <UserAvatar
                            alt={settings.username || "User"}
                            src={getProfilePic() || avatarImg}
                            sx={{
                              width: 60,
                              height: 60,
                              mx: "auto",
                              mb: 1,
                              border: `3px solid ${theme.palette.primary.main}`,
                            }}
                          />
                          <Typography variant="subtitle1" fontWeight={600}>
                            {/* {settings?.username} */}
                            {username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {/* {settings.email} */}
                            {useremail}
                          </Typography>
                        </Box>

                        {profileMenuItems.map((item) => (
                          <MenuItem
                            key={item.label}
                            onClick={() => {
                              handleProfileMenuClose();
                              item.action();
                            }}
                            sx={{
                              py: 1.5,
                              gap: 2,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.05
                                ),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color:
                                  item.label === "Log Out"
                                    ? theme.palette.error.main
                                    : theme.palette.primary.main,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{
                                fontWeight: 500,
                                color:
                                  item.label === "Log Out"
                                    ? theme.palette.error.main
                                    : "inherit",
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </>
              ) : (
                /* Login button for guests */
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<LoginIcon />}
                    component={NavLink}
                    to="/login"
                    // sx={{
                    //   borderRadius: 20,
                    //   textTransform: "none",
                    //   fontWeight: 600,
                    //   borderWidth: 2,
                    //   transition: "all 0.2s ease",
                    //   "&:hover": {
                    //     borderWidth: 2,
                    //     transform: "translateY(-2px)",
                    //     boxShadow: `0 4px 8px ${alpha(
                    //       theme.palette.primary.main,
                    //       0.25
                    //     )}`,
                    //   },
                    // }}
                    sx={{
                      borderRadius: 20,
                      textTransform: "none",
                      fontWeight: 600,
                      borderWidth: 2,
                      height: 40,
                      minWidth: 92,
                      // px: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      // gap: 1,
                      "& .MuiButton-startIcon": {
                        marginRight: 3,
                        svg: { fontSize: 18 },
                      },
                      "&:hover": {
                        borderWidth: 2,
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 8px ${alpha(
                          theme.palette.primary.main,
                          0.25
                        )}`,
                      },
                    }}
                  >
                    {isMobile ? "Login" : "Login"}
                  </Button>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Spacer to prevent content from hiding behind the fixed AppBar */}
      <Toolbar sx={{ mb: 2 }} />
    </Box>
  );
}
